import React, { useState, useEffect, useRef, useCallback } from 'react';

const BASE = 'https://menu.searchisend.in';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const inp = {
    padding: '8px 10px',
    border: '1px solid var(--border-color, #d1d5db)',
    borderRadius: '6px',
    width: '100%',
    boxSizing: 'border-box',
    fontSize: '0.9em',
    backgroundColor: 'var(--bg-color, #fff)',
    color: 'var(--text-color, #111)',
};

const btn = (color = '#ea580c', disabled = false) => ({
    padding: '10px 20px',
    background: disabled ? '#9ca3af' : color,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: '600',
    fontSize: '0.9em',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    transition: 'transform 0.15s, box-shadow 0.15s',
});

// ─── Parser ──────────────────────────────────────────────────────────────────
/**
 * Parses a single text line into a menu item.
 * Supported formats:
 *   [Category] Item Name - Price | Description
 *   Item Name - Price
 *   Item Name - Price | Description
 */
const parseLine = (line, globalCategory, globalType) => {
    line = line.trim();
    if (!line) return null;

    let category = globalCategory;
    let item_type = globalType;

    // Extract inline category: [Starters] Paneer Tikka - 220
    const catMatch = line.match(/^\[([^\]]+)\]\s*/);
    if (catMatch) {
        category = catMatch[1].trim();
        line = line.slice(catMatch[0].length);
    }

    // Extract description after pipe: Item Name - 220 | Some description
    let description = '';
    const pipeIdx = line.indexOf('|');
    if (pipeIdx !== -1) {
        description = line.slice(pipeIdx + 1).trim();
        line = line.slice(0, pipeIdx).trim();
    }

    // Split name and price on last separator  (- ; , or whitespace-number)
    const parts = line.split(/\s*[-;,]\s*/);
    if (parts.length < 2) {
        return { error: `Could not parse: "${line}" — missing separator between name and price.`, raw: line };
    }

    const priceStr = parts[parts.length - 1].replace(/[^0-9.]/g, '');
    const nameStr  = parts.slice(0, parts.length - 1).join('-').trim();

    if (!nameStr) return { error: `Missing item name.`, raw: line };
    if (!priceStr || isNaN(parseFloat(priceStr))) {
        return { error: `Invalid price in: "${line}"`, raw: line };
    }

    return {
        id: `${Date.now()}_${Math.random()}`,
        name: nameStr,
        price: parseFloat(priceStr),
        category,
        item_type,
        description,
        hasError: false,
    };
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BulkEntry = ({ token }) => {
    const [tab, setTab]               = useState('text'); // 'text' | 'import'
    const [rawText, setRawText]       = useState('');
    const [parsedItems, setParsedItems] = useState([]);
    const [parseErrors, setParseErrors] = useState([]);
    const [globalCategory, setGlobalCategory] = useState('');
    const [globalType, setGlobalType] = useState('veg');
    const [categories, setCategories] = useState([]);
    const [catLoading, setCatLoading] = useState(true);
    const [loading, setLoading]       = useState(false);
    const [message, setMessage]       = useState({ text: '', type: '' });

    // Import state
    const [importFile, setImportFile]     = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef(null);

    // ── Fetch Categories ────────────────────────────────────────────────────
    useEffect(() => {
        const fetchCats = async () => {
            try {
                const res  = await fetch(`${BASE}/apimenu/v2/v2_categories.php?action=fetch`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success && data.categories.length > 0) {
                    setCategories(data.categories);
                    setGlobalCategory(data.categories[0].category_name);
                }
            } catch {}
            finally { setCatLoading(false); }
        };
        fetchCats();
    }, [token]);

    // ── Keyboard shortcut Ctrl+Enter → parse ───────────────────────────────
    const handleKeyDown = useCallback((e) => {
        if (e.ctrlKey && e.key === 'Enter') {
            e.preventDefault();
            handleParse();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawText, globalCategory, globalType]);

    // ── Parse Text ─────────────────────────────────────────────────────────
    const handleParse = () => {
        const lines = rawText.split('\n').filter(l => l.trim() !== '');
        const newItems  = [];
        const newErrors = [];

        lines.forEach((line, i) => {
            const result = parseLine(line, globalCategory, globalType);
            if (!result) return;
            if (result.error) {
                newErrors.push(`Line ${i + 1}: ${result.error}`);
            } else {
                newItems.push(result);
            }
        });

        setParsedItems(prev => [...prev, ...newItems]);
        setParseErrors(newErrors);
        setRawText('');
    };

    // ── Upload Parsed Items ─────────────────────────────────────────────────
    const handleUpload = async () => {
        if (parsedItems.length === 0) return;
        setLoading(true);
        setMessage({ text: '', type: '' });
        try {
            const res = await fetch(`${BASE}/apimenu/bulk_upload.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ items: parsedItems })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ text: `✅ ${parsedItems.length} items uploaded successfully!`, type: 'success' });
                setParsedItems([]);
                setParseErrors([]);
            } else {
                setMessage({ text: data.message || 'Upload failed.', type: 'error' });
            }
        } catch {
            setMessage({ text: 'Network error. Please try again.', type: 'error' });
        } finally {
            setLoading(false);
            setTimeout(() => setMessage({ text: '', type: '' }), 6000);
        }
    };

    // ── Import CSV ──────────────────────────────────────────────────────────
    const handleImport = async () => {
        if (!importFile) return;
        setImportLoading(true);
        setImportResult(null);
        try {
            const fd = new FormData();
            fd.append('csv_file', importFile);
            const res = await fetch(`${BASE}/apimenu/v2/v2_menu_template.php?action=import`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd
            });
            const data = await res.json();
            setImportResult(data);
        } catch {
            setImportResult({ success: false, message: 'Network error during import.' });
        } finally {
            setImportLoading(false);
            setImportFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // ── Template Download ───────────────────────────────────────────────────
    const handleDownloadTemplate = () => {
        const url = `${BASE}/apimenu/v2/v2_menu_template.php?action=download`;
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', 'menu_template.csv');

        // Fetch with auth token and trigger download
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                a.href = blobUrl;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(blobUrl);
            });
    };

    // ── Row Actions ─────────────────────────────────────────────────────────
    const removeItem     = (id) => setParsedItems(p => p.filter(i => i.id !== id));
    const duplicateItem  = (id) => {
        const idx = parsedItems.findIndex(i => i.id === id);
        if (idx === -1) return;
        const copy = { ...parsedItems[idx], id: `${Date.now()}_${Math.random()}` };
        const next = [...parsedItems];
        next.splice(idx + 1, 0, copy);
        setParsedItems(next);
    };
    const updateItem     = (id, field, value) =>
        setParsedItems(p => p.map(i => i.id === id ? { ...i, [field]: value } : i));

    // ─── Tab button style ───────────────────────────────────────────────────
    const tabBtn = (active) => ({
        padding: '9px 20px',
        border: 'none',
        borderBottom: active ? '2px solid var(--primary-orange, #ea580c)' : '2px solid transparent',
        background: 'none',
        cursor: 'pointer',
        fontWeight: active ? '700' : '500',
        color: active ? 'var(--primary-orange, #ea580c)' : 'var(--text-color-secondary, #6b7280)',
        fontSize: '0.95em',
        transition: 'color 0.2s',
    });

    // ─── Render ─────────────────────────────────────────────────────────────
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '4px' }}>
                <div>
                    <h2 style={{ margin: '0 0 4px 0' }}>Rapid Bulk Entry</h2>
                    <p style={{ margin: 0, color: 'var(--text-color-secondary)', fontSize: '0.9em' }}>
                        Add many menu items at once — paste text or import from Excel/CSV.
                    </p>
                </div>
                <button
                    onClick={handleDownloadTemplate}
                    style={{ ...btn('#10b981'), boxShadow: '0 2px 8px rgba(16,185,129,0.25)' }}
                    title="Download a pre-filled Excel/CSV template"
                >
                    <i className="fas fa-file-download" /> Download Template
                </button>
            </div>

            {/* ── Tabs ── */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color, #e5e7eb)', marginBottom: '24px', marginTop: '20px' }}>
                <button style={tabBtn(tab === 'text')}   onClick={() => setTab('text')}>
                    <i className="fas fa-keyboard" style={{ marginRight: '6px' }} />Paste Text
                </button>
                <button style={tabBtn(tab === 'import')} onClick={() => setTab('import')}>
                    <i className="fas fa-file-upload" style={{ marginRight: '6px' }} />Import CSV / Excel
                </button>
            </div>

            {/* ── Global Feedback ── */}
            {message.text && (
                <div style={{
                    padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontWeight: '500',
                    backgroundColor: message.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: message.type === 'success' ? '#065f46' : '#991b1b',
                    border: `1px solid ${message.type === 'success' ? '#6ee7b7' : '#fca5a5'}`
                }}>
                    {message.text}
                </div>
            )}

            {/* ════════════════════════ TAB: PASTE TEXT ════════════════════════ */}
            {tab === 'text' && (
                <div>
                    {/* Controls */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '16px' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '0.88em', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-color-secondary)' }}>
                                Default Category
                            </label>
                            {catLoading ? (
                                <input value="Loading…" disabled style={inp} />
                            ) : categories.length === 0 ? (
                                <input value={globalCategory} onChange={e => setGlobalCategory(e.target.value)} placeholder="Type category name" style={inp} />
                            ) : (
                                <select value={globalCategory} onChange={e => setGlobalCategory(e.target.value)} style={inp}>
                                    {categories.map(c => <option key={c.category_id} value={c.category_name}>{c.category_name}</option>)}
                                </select>
                            )}
                        </div>
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '0.88em', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-color-secondary)' }}>
                                Default Type
                            </label>
                            <select value={globalType} onChange={e => setGlobalType(e.target.value)} style={inp}>
                                <option value="veg">🟢 Veg</option>
                                <option value="non-veg">🔴 Non-Veg</option>
                                <option value="addon">⚪ Add-on</option>
                            </select>
                        </div>
                    </div>

                    {/* Format guide */}
                    <div style={{ background: 'var(--bg-color, #f9fafb)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px', padding: '12px 16px', marginBottom: '14px', fontSize: '0.88em', color: 'var(--text-color-secondary)' }}>
                        <strong style={{ color: 'var(--text-color)' }}>Format Guide:</strong>
                        <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px', fontFamily: 'monospace' }}>
                            <span>Item Name - Price</span>
                            <span style={{ color: '#9ca3af' }}>[Category] Item Name - Price</span>
                            <span style={{ color: '#9ca3af' }}>Item Name - Price | Description</span>
                            <span style={{ color: '#9ca3af' }}>[Starters] Paneer Tikka - 220 | Grilled paneer with spices</span>
                        </div>
                        <div style={{ marginTop: '8px', fontSize: '0.92em' }}>
                            <kbd style={{ background: '#e5e7eb', padding: '1px 6px', borderRadius: '4px', border: '1px solid #d1d5db' }}>Ctrl</kbd>
                            {' + '}
                            <kbd style={{ background: '#e5e7eb', padding: '1px 6px', borderRadius: '4px', border: '1px solid #d1d5db' }}>Enter</kbd>
                            {' to Parse'}
                        </div>
                    </div>

                    <textarea
                        value={rawText}
                        onChange={e => setRawText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={
                            "Paste items here, one per line...\n" +
                            "Paneer Butter Masala - 220\n" +
                            "[Biryani] Chicken Biryani - 299\n" +
                            "Dal Makhani - 180 | Slow-cooked black lentils"
                        }
                        style={{
                            width: '100%', height: '180px', padding: '14px',
                            border: '1px solid var(--border-color, #d1d5db)',
                            borderRadius: '8px', marginBottom: '12px',
                            fontFamily: 'monospace', fontSize: '0.95em',
                            boxSizing: 'border-box', resize: 'vertical',
                            backgroundColor: 'var(--bg-color, #fff)',
                            color: 'var(--text-color, #111)',
                        }}
                    />

                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                        <button
                            onClick={handleParse}
                            disabled={!rawText.trim()}
                            style={btn('#3b82f6', !rawText.trim())}
                        >
                            <i className="fas fa-magic" /> Parse Text
                            {rawText.trim() && (
                                <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '50px', padding: '1px 8px', fontSize: '0.85em' }}>
                                    {rawText.split('\n').filter(l => l.trim()).length} lines
                                </span>
                            )}
                        </button>
                        {parsedItems.length > 0 && (
                            <span style={{ color: 'var(--text-color-secondary)', fontSize: '0.9em' }}>
                                <i className="fas fa-check-circle" style={{ color: '#10b981', marginRight: '5px' }} />
                                {parsedItems.length} item{parsedItems.length !== 1 ? 's' : ''} ready
                            </span>
                        )}
                    </div>

                    {/* Parse Errors */}
                    {parseErrors.length > 0 && (
                        <div style={{ marginTop: '14px', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '0.88em' }}>
                            <strong style={{ color: '#991b1b' }}>⚠️ {parseErrors.length} line{parseErrors.length !== 1 ? 's' : ''} skipped:</strong>
                            <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px', color: '#b91c1c' }}>
                                {parseErrors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        </div>
                    )}

                    {/* Preview Table */}
                    {parsedItems.length > 0 && (
                        <div style={{ marginTop: '28px', borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                                <h3 style={{ margin: 0, fontSize: '1.05em' }}>
                                    Preview — {parsedItems.length} item{parsedItems.length !== 1 ? 's' : ''} ready to upload
                                </h3>
                                <button
                                    onClick={() => { setParsedItems([]); setParseErrors([]); }}
                                    style={{ background: 'none', border: '1px solid var(--border-color, #d1d5db)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', color: 'var(--red-accent, #ef4444)', fontWeight: '600', fontSize: '0.88em' }}
                                >
                                    Clear All
                                </button>
                            </div>
                            <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid var(--border-color, #e5e7eb)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                                    <thead style={{ background: 'var(--bg-color, #f9fafb)' }}>
                                        <tr>
                                            {['Category', 'Item Name', 'Price (₹)', 'Type', 'Description', ''].map(h => (
                                                <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-color, #e5e7eb)', textAlign: 'left', fontSize: '0.8em', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-color-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parsedItems.map((item, idx) => (
                                            <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? 'var(--card-bg-color, #fff)' : 'var(--bg-color, #f9fafb)' }}>
                                                <td style={{ padding: '7px 10px', minWidth: '130px' }}>
                                                    {categories.length > 0 ? (
                                                        <select value={item.category} onChange={e => updateItem(item.id, 'category', e.target.value)} style={{ ...inp, marginBottom: 0 }}>
                                                            {categories.map(c => <option key={c.category_id} value={c.category_name}>{c.category_name}</option>)}
                                                        </select>
                                                    ) : (
                                                        <input value={item.category} onChange={e => updateItem(item.id, 'category', e.target.value)} style={{ ...inp, marginBottom: 0 }} />
                                                    )}
                                                </td>
                                                <td style={{ padding: '7px 10px', minWidth: '160px' }}>
                                                    <input value={item.name} onChange={e => updateItem(item.id, 'name', e.target.value)} style={{ ...inp, marginBottom: 0 }} />
                                                </td>
                                                <td style={{ padding: '7px 10px', minWidth: '90px' }}>
                                                    <input type="number" step="0.01" value={item.price} onChange={e => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)} style={{ ...inp, marginBottom: 0, width: '80px' }} />
                                                </td>
                                                <td style={{ padding: '7px 10px', minWidth: '110px' }}>
                                                    <select value={item.item_type} onChange={e => updateItem(item.id, 'item_type', e.target.value)} style={{ ...inp, marginBottom: 0 }}>
                                                        <option value="veg">🟢 Veg</option>
                                                        <option value="non-veg">🔴 Non-Veg</option>
                                                        <option value="addon">⚪ Add-on</option>
                                                    </select>
                                                </td>
                                                <td style={{ padding: '7px 10px', minWidth: '160px' }}>
                                                    <input value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} placeholder="Optional…" style={{ ...inp, marginBottom: 0 }} />
                                                </td>
                                                <td style={{ padding: '7px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    <button
                                                        onClick={() => duplicateItem(item.id)}
                                                        title="Duplicate row"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px 6px', fontSize: '0.95em' }}
                                                    >
                                                        <i className="fas fa-copy" />
                                                    </button>
                                                    <button
                                                        onClick={() => removeItem(item.id)}
                                                        title="Remove row"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px 6px', fontSize: '0.95em' }}
                                                    >
                                                        <i className="fas fa-trash" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <button
                                onClick={handleUpload}
                                disabled={loading}
                                style={{
                                    ...btn('#ea580c', loading),
                                    marginTop: '18px', width: '100%',
                                    justifyContent: 'center', padding: '14px',
                                    fontSize: '1em', borderRadius: '8px',
                                    boxShadow: loading ? 'none' : '0 4px 14px rgba(234,88,12,0.3)',
                                }}
                            >
                                <i className="fas fa-upload" />
                                {loading ? 'Uploading…' : `Upload ${parsedItems.length} Items to Menu`}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ════════════════════════ TAB: IMPORT ════════════════════════ */}
            {tab === 'import' && (
                <div>
                    {/* Download prompt */}
                    <div style={{ background: 'linear-gradient(135deg, #eff6ff, #f0fdf4)', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px 24px', marginBottom: '28px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <h3 style={{ margin: '0 0 6px 0', fontSize: '1.05em', color: '#1e40af' }}>
                                <i className="fas fa-file-excel" style={{ marginRight: '8px', color: '#16a34a' }} />
                                Start with the Template
                            </h3>
                            <p style={{ margin: 0, fontSize: '0.9em', color: '#374151' }}>
                                Download the template, fill it in Excel or Google Sheets, then upload it below.
                                Supports single price or up to 3 size variants per item.
                            </p>
                        </div>
                        <button onClick={handleDownloadTemplate} style={{ ...btn('#16a34a'), whiteSpace: 'nowrap', boxShadow: '0 2px 10px rgba(22,163,74,0.25)' }}>
                            <i className="fas fa-file-download" /> Download Template
                        </button>
                    </div>

                    {/* Column guide */}
                    <div style={{ marginBottom: '24px', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-color, #f9fafb)' }}>
                                    {['Column', 'Required?', 'Example'].map(h => (
                                        <th key={h} style={{ padding: '8px 12px', borderBottom: '2px solid var(--border-color, #e5e7eb)', textAlign: 'left', fontWeight: '700', color: 'var(--text-color-secondary)' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ['Item Name', '✅ Yes', 'Paneer Butter Masala'],
                                    ['Category', '✅ Yes', 'Main Course'],
                                    ['Type', '✅ Yes', 'veg / non-veg / addon'],
                                    ['Description', 'Optional', 'Rich creamy curry'],
                                    ['Single Price (₹)', 'Required if no sizes', '250'],
                                    ['Size 1 Name + Size 1 Price', 'Optional', 'Half, 199'],
                                    ['Size 2 Name + Size 2 Price', 'Optional', 'Full, 349'],
                                    ['Size 3 Name + Size 3 Price', 'Optional', 'Family, 549'],
                                ].map(([col, req, ex]) => (
                                    <tr key={col}>
                                        <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--border-color, #f3f4f6)', fontWeight: '600' }}>{col}</td>
                                        <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--border-color, #f3f4f6)', color: req.includes('Yes') ? '#15803d' : '#6b7280' }}>{req}</td>
                                        <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--border-color, #f3f4f6)', fontFamily: 'monospace', color: '#4b5563' }}>{ex}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* File Upload */}
                    <div style={{ border: '2px dashed var(--border-color, #d1d5db)', borderRadius: '12px', padding: '32px', textAlign: 'center', background: importFile ? '#f0fdf4' : 'var(--bg-color, #f9fafb)', transition: 'background 0.2s' }}>
                        <i className="fas fa-cloud-upload-alt" style={{ fontSize: '2.5rem', color: importFile ? '#16a34a' : '#9ca3af', marginBottom: '12px', display: 'block' }} />
                        {importFile ? (
                            <div>
                                <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#15803d' }}>{importFile.name}</p>
                                <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '0.88em' }}>
                                    {(importFile.size / 1024).toFixed(1)} KB — ready to import
                                </p>
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button onClick={handleImport} disabled={importLoading} style={{ ...btn('#ea580c', importLoading), boxShadow: '0 3px 10px rgba(234,88,12,0.3)' }}>
                                        <i className="fas fa-upload" />
                                        {importLoading ? 'Importing…' : 'Import Now'}
                                    </button>
                                    <button onClick={() => { setImportFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} style={{ ...btn('#6b7280') }}>
                                        <i className="fas fa-times" /> Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <p style={{ margin: '0 0 12px 0', color: 'var(--text-color-secondary)' }}>
                                    Drop your CSV file here, or click to browse
                                </p>
                                <label style={{ ...btn('#3b82f6'), cursor: 'pointer', display: 'inline-flex' }}>
                                    <i className="fas fa-folder-open" /> Choose File (.csv)
                                    <input
                                        type="file"
                                        accept=".csv,text/csv"
                                        ref={fileInputRef}
                                        onChange={e => setImportFile(e.target.files[0] || null)}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Import Result */}
                    {importResult && (
                        <div style={{
                            marginTop: '20px', padding: '16px 20px', borderRadius: '10px',
                            border: `1px solid ${importResult.success ? '#6ee7b7' : '#fca5a5'}`,
                            background: importResult.success ? '#d1fae5' : '#fee2e2',
                        }}>
                            <p style={{ margin: '0 0 8px 0', fontWeight: '700', color: importResult.success ? '#065f46' : '#991b1b' }}>
                                {importResult.success ? '✅' : '❌'} {importResult.message}
                            </p>
                            {importResult.success && (
                                <p style={{ margin: 0, fontSize: '0.9em', color: '#047857' }}>
                                    <strong>{importResult.inserted}</strong> inserted &nbsp;·&nbsp;
                                    <strong>{importResult.skipped}</strong> skipped
                                </p>
                            )}
                            {importResult.errors && importResult.errors.length > 0 && (
                                <ul style={{ margin: '10px 0 0 0', paddingLeft: '18px', fontSize: '0.88em', color: '#b91c1c' }}>
                                    {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BulkEntry;
